import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useConversationHistory } from '../hooks/useConversationHistory.ts'
import { useConversationalMode } from '../hooks/useConversationalMode.ts'
import { useMessageLimits } from '../hooks/useMessageLimits.ts'
import { analyzeWithLLM } from '../services/llmService.ts'
import { loadConversations } from '../utils/conversationStorage.ts'
import type { Message } from '../utils/conversationStorage.ts'
import { ACTION_ID_TO_AGENT, AGENT_NAMES } from '../constants/agentNames.ts'
import { AGENT_COLLECTED_DATA } from '../constants/agentIntents.ts'
import {
  hasPendingImage,
  getPendingImage,
  setPendingImageFromBlob,
  getPendingImageAsFile,
  clearPendingImage
} from '../utils/agentImageStorage.ts'
import { compressImage } from '../utils/imageCompression.ts'

export interface ConversationalAgentContextValue {
  currentSession: ReturnType<typeof useConversationHistory>['currentSession']
  allSessions: ReturnType<typeof useConversationHistory>['allSessions']
  createSession: ReturnType<typeof useConversationHistory>['createSession']
  loadSession: ReturnType<typeof useConversationHistory>['loadSession']
  addMessage: ReturnType<typeof useConversationHistory>['addMessage']
  removeSession: ReturnType<typeof useConversationHistory>['removeSession']
  clearCurrentSession: ReturnType<typeof useConversationHistory>['clearCurrentSession']
  isConversationalModeActive: boolean
  activateConversationalMode: ReturnType<typeof useConversationalMode>['activate']
  deactivateConversationalMode: ReturnType<typeof useConversationalMode>['deactivate']
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  sendMessage: (message: string, options?: { confirmExecution?: boolean }) => Promise<void>
  isProcessing: boolean
  userId: string | undefined
  userData: any
  activeAgent: string
  setActiveAgent: (agent: string) => void
  collectedData: Record<string, any> | null
  setCollectedData: (data: Record<string, any> | null) => void
  missingFields: string[]
  readyToExecute: boolean
  setMissingFields: (fields: string[]) => void
  setReadyToExecute: (v: boolean) => void
  hasPendingImage: boolean
  imageUploadAcknowledged: boolean
  setImageUploadAcknowledged: (v: boolean) => void
  storePendingImage: (file: File) => Promise<void>
  clearPendingImage: () => void
  pendingImagePreview: string | null
}

const ConversationalAgentContext = createContext<ConversationalAgentContextValue | null>(null)

export function useConversationalAgent(): ConversationalAgentContextValue {
  const value = useContext(ConversationalAgentContext)
  if (!value) {
    throw new Error('useConversationalAgent must be used within ConversationalAgentProvider')
  }
  return value
}

export function useConversationalAgentOptional(): ConversationalAgentContextValue | null {
  return useContext(ConversationalAgentContext)
}

interface ConversationalAgentProviderProps {
  children: React.ReactNode
}

export function ConversationalAgentProvider({ children }: ConversationalAgentProviderProps) {
  const location = useLocation()
  const userData = useSelector((state: any) => state.general?.userData)
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const userId = userData?.id

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession
  } = useConversationHistory(userId)

  const {
    isActive: isConversationalModeActive,
    activate: activateConversationalMode,
    deactivate: deactivateConversationalMode
  } = useConversationalMode()

  const { canSendMessage, remainingMessages, hasReachedLimit, limit } = useMessageLimits(
    currentSession,
    isUserPremium
  )

  const [isProcessing, setIsProcessing] = useState(false)
  const [activeAgent, setActiveAgent] = useState<string>(AGENT_NAMES.general)
  const [collectedData, setCollectedData] = useState<Record<string, any> | null>(null)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [readyToExecute, setReadyToExecute] = useState(false)
  const [imageUploadAcknowledged, setImageUploadAcknowledged] = useState(false)
  const [, forceImagePreviewUpdate] = useState(0)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)

  const sessionId = currentSession?.sessionId

  useEffect(() => {
    setPendingImageFile(null)
    setReadyToExecute(false)
    setCollectedData(null)
    setMissingFields([])
    setActiveAgent(AGENT_NAMES.general)
  }, [sessionId])

  useEffect(() => {
    const pathname = location.pathname
    const fromConversation = (location.state as any)?.fromConversation === true
    const sessionIdFromUrl = new URLSearchParams(location.search).get('session')
    if (pathname !== '/dashboard' && !fromConversation && !sessionIdFromUrl) {
      clearCurrentSession()
    }
  }, [location.pathname, (location.state as any)?.fromConversation, location.search])

  useEffect(() => {
    setImageUploadAcknowledged(false)
  }, [sessionId])

  const storePendingImage = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) return
      setPendingImageFile(file)
      const blob = await compressImage(file)
      await setPendingImageFromBlob(blob, sessionId, userId)
      forceImagePreviewUpdate((n) => n + 1)
    },
    [sessionId, userId]
  )

  const pendingImage = hasPendingImage(sessionId, userId)

  const clearPendingImageFn = useCallback(() => {
    setPendingImageFile(null)
    clearPendingImage(sessionId, userId)
    setImageUploadAcknowledged(false)
    forceImagePreviewUpdate((n) => n + 1)
  }, [sessionId, userId])

  const sendMessage = useCallback(
    async (message: string, options?: { confirmExecution?: boolean }) => {
      if (!canSendMessage && !options?.confirmExecution) return

      const conversations = loadConversations(userId)
      let session = conversations.currentSessionId
        ? conversations.sessions.find((s) => s.sessionId === conversations.currentSessionId) || null
        : null
      if (!session) {
        session = createSession()
      }

      const targetSessionId = session.sessionId

      const msgContent = options?.confirmExecution ? '' : message.trim()
      if (!msgContent && !options?.confirmExecution) return

      const userMessage: Message = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: options?.confirmExecution ? 'Confirmar creación' : message,
        timestamp: new Date().toISOString()
      }

      addMessage(userMessage, targetSessionId)
      setIsProcessing(true)

      await new Promise((resolve) => setTimeout(resolve, 100))

      try {
        const freshConversations = loadConversations(userId)
        const currentSessionForLLM =
          freshConversations.sessions.find((s) => s.sessionId === targetSessionId) || session

        const allMessages = currentSessionForLLM.messages || []
        const messagesWithoutCurrent =
          allMessages.length > 0 && allMessages[allMessages.length - 1].id === userMessage.id
            ? allMessages.slice(0, -1)
            : allMessages
        const previousMessages = messagesWithoutCurrent.slice(-14)
        const historyForLLM = previousMessages.map((m) => {
          let content = m.role === 'assistant' && m.llmRawResponse ? m.llmRawResponse : m.content
          if (m.role === 'assistant' && m.llmRawResponse) {
            try {
              const parsed = JSON.parse(m.llmRawResponse)
              if ('imagePreviewUrl' in parsed) {
                const { imagePreviewUrl: _, ...rest } = parsed
                content = JSON.stringify(rest)
              }
            } catch {
            }
          }
          return { role: m.role, content }
        })

        const payloadCollectedData =
          collectedData ?? AGENT_COLLECTED_DATA[activeAgent]
        const hasStoredImage = hasPendingImage(targetSessionId, userId)
        const imageUploaded = hasStoredImage
        const doConfirm = options?.confirmExecution === true
        const payloadWithImage = { ...payloadCollectedData }
        if (imageUploaded && !doConfirm) {
          payloadWithImage.image_uploaded = true
          setImageUploadAcknowledged(true)
        }
        let imageToSend: File | undefined
        if (doConfirm && imageUploaded) {
          imageToSend = pendingImageFile ?? getPendingImageAsFile(targetSessionId, userId) ?? undefined
        }

        const llmResult = await analyzeWithLLM(msgContent || (doConfirm ? 'Confirmar' : message), historyForLLM, {
          activeAgent,
          collectedData: payloadWithImage,
          imageUploaded,
          readyToExecute: doConfirm,
          image: imageToSend
        })

        if (doConfirm && hasStoredImage) {
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
        }
        if ((llmResult as any).collectedData) {
          setCollectedData(prev => ({ ...prev, ...(llmResult as any).collectedData }))
        }
        if (Array.isArray((llmResult as any).missingFields)) {
          setMissingFields((llmResult as any).missingFields)
        }
        const isCreatedPortfolio = (llmResult as any).data &&
          typeof (llmResult as any).data.id !== 'undefined' &&
          Array.isArray((llmResult as any).data.portfolio_images)
        if ((llmResult as any).readyToExecute !== undefined) {
          setReadyToExecute((llmResult as any).readyToExecute === true)
        } else if ((llmResult as any).delegate === 'general-agent' || isCreatedPortfolio) {
          setReadyToExecute(false)
        }
        if (llmResult.actionId && ACTION_ID_TO_AGENT[llmResult.actionId]) {
          const nextAgent = ACTION_ID_TO_AGENT[llmResult.actionId]
          setActiveAgent(nextAgent)
          if (!(llmResult as any).collectedData) {
            const defaultCollected = AGENT_COLLECTED_DATA[nextAgent] || null
            setCollectedData(defaultCollected)
          }
        }
        if ((llmResult as any).delegate === 'general-agent') {
          setActiveAgent(AGENT_NAMES.general)
          setCollectedData(null)
          setMissingFields([])
          setReadyToExecute(false)
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
        }
        if (isCreatedPortfolio) {
          setCollectedData(null)
          setMissingFields([])
        }

        const llmRawResponseObj: any = {
          actionId: llmResult.actionId,
          confidence: llmResult.confidence,
          response: llmResult.response,
          guideMode: llmResult.guideMode || false
        }

        if ((llmResult as any).missingFields) {
          llmRawResponseObj.missingFields = (llmResult as any).missingFields
        }
        if ((llmResult as any).collectedData) {
          llmRawResponseObj.collectedData = (llmResult as any).collectedData
        }
        if ((llmResult as any).readyToExecute !== undefined) {
          llmRawResponseObj.readyToExecute = (llmResult as any).readyToExecute
        }
        if (
          llmResult.actionId === 'guardar-portfolio-item' &&
          (llmResult as any).collectedData?.image_uploaded === true
        ) {
          const previewUrl = getPendingImage(targetSessionId, userId)
          if (previewUrl) llmRawResponseObj.imagePreviewUrl = previewUrl
        }

        if (llmResult.needsData && llmResult.data) {
          llmRawResponseObj.needsData = true
          llmRawResponseObj.data = llmResult.data
          llmRawResponseObj.total = llmResult.total || 0
          if ((llmResult as any).quotationType)
            llmRawResponseObj.quotationType = (llmResult as any).quotationType
          if ((llmResult as any).workVisibility)
            llmRawResponseObj.workVisibility = (llmResult as any).workVisibility
          if ((llmResult as any).isPublic !== undefined)
            llmRawResponseObj.isPublic = (llmResult as any).isPublic
          if (llmResult.noResultsMessage)
            llmRawResponseObj.noResultsMessage = llmResult.noResultsMessage
          if ((llmResult as any).searchTerm)
            llmRawResponseObj.searchTerm = (llmResult as any).searchTerm
          if ((llmResult as any).projectType)
            llmRawResponseObj.projectType = (llmResult as any).projectType
        }

        const createdPortfolio = isCreatedPortfolio
          ? {
              ...(llmResult as any).data,
              type:
                (llmResult as any).data?.type ??
                (collectedData?.type as 'producto' | 'servicio' | undefined)
            }
          : undefined

        const llmRawResponse = JSON.stringify(llmRawResponseObj)

        const assistantMessage: Message = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: llmResult.response,
          timestamp: new Date().toISOString(),
          actionId: llmResult.actionId || null,
          guideMode: llmResult.guideMode || false,
          llmRawResponse,
          ...(createdPortfolio && { createdPortfolio }),
          ...(llmResult.needsData &&
            llmResult.data && {
              data: llmResult.data,
              dataTotal: llmResult.total || 0,
              needsData: true,
              quotationType: (llmResult as any).quotationType,
              workVisibility: (llmResult as any).workVisibility,
              isPublic: (llmResult as any).isPublic,
              noResultsMessage: llmResult.noResultsMessage
            })
        }

        addMessage(assistantMessage)
      } catch (error) {
        const errorMsg: Message = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content:
            'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?',
          timestamp: new Date().toISOString()
        }
        addMessage(errorMsg)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      canSendMessage,
      createSession,
      addMessage,
      userId,
      collectedData,
      activeAgent,
      pendingImageFile
    ]
  )

  const value: ConversationalAgentContextValue = {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    sendMessage,
    isProcessing,
    userId,
    userData,
    activeAgent,
    setActiveAgent,
    collectedData,
    setCollectedData,
    missingFields,
    readyToExecute,
    setMissingFields,
    setReadyToExecute,
    hasPendingImage: pendingImage,
    imageUploadAcknowledged,
    setImageUploadAcknowledged,
    storePendingImage,
    clearPendingImage: clearPendingImageFn,
    pendingImagePreview: getPendingImage(sessionId, userId)
  }

  return (
    <ConversationalAgentContext.Provider value={value}>
      {children}
    </ConversationalAgentContext.Provider>
  )
}
